import {SleepAsync} from "js-vextensions";
import {O, RunInAction, RunInAction_Set} from "web-vcore";
import {autorun} from "mobx";
import {EngineConfig_Visualization, PromptPool} from "../../../../Server/DB/engineConfigs/@EngineConfig/@EC_Visualization.js";
import {store} from "../../../../Store/index.js";

// original headers: "Word","Bigram","Conc.M","Conc.SD","Unknown","Total","Percent_known","SUBTLEX","Dom_Pos"
export interface WordEntry_Brysbaert {
	word: string;
	bigram: boolean;
	concM: number;
	concSD: number;
	unknown: number;
	total: number;
	percentKnown: number;
	subtlex: number;
	/** dominant part of speech, as a STRING in the data ("Noun", "Adjective", "#N/A") */
	domPos: string;
	tags: string[]; // custom-added
}

class WordListsStore {
	@O accessor wordList_loading = false; // this doesn't usually need to be observable; but do so, as this lets it auto-retry if fetch hit an error
	@O accessor wordList_cached: WordEntry_Brysbaert[]|null = null;
}
const wordListsStore = new WordListsStore();

export function GetWordList_MobX(): WordEntry_Brysbaert[]|null {
	if (!wordListsStore.wordList_loading && wordListsStore.wordList_cached == null) {
		RunInAction_Set(()=>wordListsStore.wordList_loading = true);
		(async()=>{
			try {
				const newWordList = await GetWordList();
				RunInAction_Set(()=>wordListsStore.wordList_cached = newWordList);
			} finally {
				// regardless of if succeeded or failed, mark loading as false now (if succeeded, next call returns cache; if failed, next call tries again)
				RunInAction_Set(()=>wordListsStore.wordList_loading = false);
			}
		})();
	}
	return wordListsStore.wordList_cached;
}
export async function GetWordList(): Promise<WordEntry_Brysbaert[]> {
	const response = await fetch("/JSON/WordConcreteness_Sorted_Tagged.json" + store.main.settings.web.fetchSuffix);
	const data = await response.json();
	return data.slice(1).map((item: any)=>({
		word: item[0],
		bigram: item[1] == 1,
		concM: item[2],
		concSD: item[3],
		unknown: item[4],
		total: item[5],
		percentKnown: item[6],
		subtlex: item[7],
		domPos: item[8],
		tags: item[9] ? item[9].split(",").map(a=>a.trim()) : [],
	})).OrderByDescending(a=>a.concM);
}

export function FilterWordList_ForVisualization(wordList: WordEntry_Brysbaert[], config: Pick<PromptPool, "concreteness_min" | "percentKnown_min" | "exclusionTags" | "mergeDuplicates">, catchErrors: boolean): string[] {
	const proceed = ()=>{
		const wordAsKey = (word: string)=>{
			return config.mergeDuplicates ? word.replace(/[\s-]/g, "") : word;
		};

		// group all entries by word-key (if grouping is enabled, this is the word with its spaces/hyphens removed; else, the original)
		const wordEntries = new Map<string, WordEntry_Brysbaert[]>();
		for (const entry of wordList) {
			const word_key = wordAsKey(entry.word);
			if (!wordEntries.has(word_key)) {
				wordEntries.set(word_key, []);
			}
			wordEntries.get(word_key)!.push(entry);
		}

		// filter words, while also merging "duplicates" (if merging was enabled)
		const validWords: string[] = [];
		for (const [word_key, entries] of wordEntries) {
			// require *at least one* entry/variant to satisfy the concreteness and percent-known requirements simultaneously
			if (!entries.some(e=>e.concM >= config.concreteness_min! && e.percentKnown >= config.percentKnown_min!)) continue;

			// require *all* entries/variants to pass the exclusion-tags requirement (ie. for none to have an excluded tag)
			// (reason: so far, these tags were assigned based on word/character-sequence alone)
			const allTags = new Set<string>(entries.flatMap(a=>a.tags));
			if (config.exclusionTags!.some(tag=>allTags.has(tag))) continue;

			const word_full = entries.map(a=>a.word).OrderByDescending(a=>a.length)[0]; // pick longest variant as "full" word
			validWords.push(word_full);
		}

		return validWords;
	};
	if (catchErrors) {
		try {
			return proceed();
		} catch (e) {
			console.error("Error in FilterWordList_ForVisualization:", e);
			return [];
		}
	}
	return proceed();
}